/**
 * useQuery hook for the paginated, filtered Publication List.
 *
 * Replaces `publicationListEffects.getAllPublications` (compose-app). Mirrors
 * the same Hasura GraphQL document and where/order builders.
 *
 * Pass `null` to disable the query — used during route transitions before
 * `entityId > 0` (compose-app-router race-condition guard, see
 * COMPOSE_RETIREMENT_GUIDELINES.md §9).
 *
 * Filter/sort/pagination params are part of the query key, so changing any
 * of them automatically refetches without manual coordination. Mutation
 * hooks (create/delete) invalidate the broad `[...all, 'list']` to refresh
 * the active page after a write.
 *
 * Errors surface via `useToast` (compose-app `viewActionCreators` retired
 * per guidelines §8). The caller passes copy in via `errorMessages`.
 *
 * Auth note: this hook intentionally does NOT use `useGraphQLQuery` from
 * `@opengov/bnp-core`. The publication list is reachable by non-admin users,
 * and the stories-service Hasura proxy maps non-admin Auth0 tokens to a
 * Hasura role that lacks `publications_aggregate` permission. Falling back
 * to `postQuery` keeps the cookie-session auth used by the retired effect.
 * SetupPDF used `useGraphQLQuery` only because that view is admin-only.
 */

import { useQuery } from '@tanstack/react-query';
import { DEFAULT_PER_PAGE } from '@opengov/stories-shared/constants';
import { IPublication } from '@opengov/stories-shared/types';

import { postQuery } from '../../../../effects/publications/postQuery';
import { useToast } from '../../../Toast/context/useToast.context';
import { orderBy } from '../../../../utils';
import {
  buildWhereClauseForSubjectPublicationClaims,
  ISubjectPublicationClaimsWhereInput
} from '../../../../utils/buildPublicationClaimsWhereClause';

import { publicationListQueryKeys, PublicationListQueryKeyParams } from './publicationListQueryKeys';
import { IPublicationListing } from '../../../../../types';

export type UseGetAllPublicationsParams = PublicationListQueryKeyParams | null;

export type GetAllPublicationsErrorMessages = {
  title: string;
  body: string;
};

type ServerResponse = {
  count: { aggregate: { count: number } };
  resources: IPublication[];
};

type Variables = {
  limit: number;
  offset: number;
  where: ReturnType<typeof buildWhereClauseForSubjectPublicationClaims>;
  order?: { [key: string]: string } | { [key: string]: string }[];
};

const GET_ALL_PUBLICATIONS_QUERY = `
  query GetAllPublicationsForList(
    $order: [publications_order_by!]
    $limit: Int!
    $offset: Int!
    $where: publications_bool_exp!
  ) {
    count: publications_aggregate(where: $where) {
      aggregate {
        count
      }
    }
    resources: publications(order_by: $order, limit: $limit, offset: $offset, where: $where) {
      createdByUser {
        firstName
        lastName
        _id
        opengovId
        email
      }
      createdAt
      createdBy
      description
      id
      publishedAt
      publishedBy
      status
      thumbnailPath
      title
      updatedAt
      updatedBy
      publicUrl
      entityId
      foldersCount: storyFolders_aggregate(where: { deletedAt: { _is_null: true } }) {
        aggregate {
          count
        }
      }
    }
  }
`;

export function useGetAllPublications(
  params: UseGetAllPublicationsParams,
  errorMessages: GetAllPublicationsErrorMessages
) {
  const { toggleToast } = useToast();

  return useQuery<ServerResponse, Error, IPublicationListing>({
    queryKey: params
      ? publicationListQueryKeys.list(params)
      : ([...publicationListQueryKeys.all, 'list', 'disabled'] as const),
    queryFn: async () => {
      if (!params) throw new Error('Disabled');
      const variables = buildVariables(params);
      return (await postQuery(GET_ALL_PUBLICATIONS_QUERY, variables)) as ServerResponse;
    },
    enabled: params !== null,
    staleTime: 30_000,
    select: (data: ServerResponse): IPublicationListing => ({
      currentPage: params?.page ?? 1,
      data: data.resources ?? [],
      total: data.count?.aggregate?.count ?? 0
    }),
    onError: () => {
      toggleToast({
        title: errorMessages.title,
        body: errorMessages.body,
        severity: 'error'
      });
    }
  });
}

function buildVariables(params: PublicationListQueryKeyParams): Variables {
  const {
    entityId,
    page,
    perPage,
    publicationVisibility,
    ogId,
    shouldFilterByUser,
    sort,
    title,
    storiesGranularAccessEnabled,
    isAdmin,
    userGroupIds,
    subjectId
  } = params;

  const whereInput: ISubjectPublicationClaimsWhereInput = {
    entityId,
    state: publicationVisibility === 'all' ? undefined : publicationVisibility,
    opengovId: shouldFilterByUser ? ogId : undefined,
    title: title ? `%${title}%` : undefined,
    userAndGroupIds:
      storiesGranularAccessEnabled && !isAdmin ? [...userGroupIds, subjectId] : undefined
  };

  const variables: Variables = {
    limit: perPage,
    offset: (page - 1) * (perPage || DEFAULT_PER_PAGE),
    where: buildWhereClauseForSubjectPublicationClaims(whereInput)
  };

  if (sort) {
    variables.order = orderBy(sort);
  }

  return variables;
}
